import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";
import { TrendingDown, TrendingUp, DollarSign } from "lucide-react";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const first = payload.find(p => p.dataKey === "First 7 Days")?.value || 0;
    const last = payload.find(p => p.dataKey === "Last 7 Days")?.value || 0;
    const change = first > 0 ? ((last - first) / first * 100).toFixed(1) : 0;
    const isGoodChange = last < first;
    
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-lg">
        <p className="font-heading text-sm mb-3 text-slate-800 font-semibold">{label}</p>
        <div className="space-y-2">
          <p className="text-sm text-slate-600">
            First 7 Days: <span className="font-semibold text-slate-800">${first.toFixed(2)}</span>
          </p>
          <p className="text-sm text-blue-600">
            Last 7 Days: <span className="font-semibold">${last.toFixed(2)}</span>
          </p>
          <div className={`flex items-center gap-1 pt-2 border-t border-slate-200 ${
            isGoodChange ? 'text-emerald-600' : 'text-red-600'
          }`}>
            {isGoodChange ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
            <span className="text-sm font-bold">
              {change}% {isGoodChange ? '(Improved)' : '(Increased)'}
            </span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export const CostEfficiencyChart = ({ 
  data,
  title = "Cost Efficiency",
  invertColors = true
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="chart-container p-6"
      data-testid={`cost-chart-${title.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-800 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-blue-500" />
            {title}
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Cost per Conversion Analysis
          </p>
        </div>
      </div>
      
      <div style={{ width: '100%', height: 300 }}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            barCategoryGap="20%"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis 
              dataKey="metric" 
              tick={{ fill: '#64748B', fontSize: 12, fontFamily: 'DM Sans' }}
              axisLine={{ stroke: '#E2E8F0' }}
              tickLine={{ stroke: '#E2E8F0' }}
            />
            <YAxis 
              tick={{ fill: '#64748B', fontSize: 12, fontFamily: 'DM Sans' }}
              axisLine={{ stroke: '#E2E8F0' }}
              tickLine={{ stroke: '#E2E8F0' }}
              tickFormatter={(value) => `$${value}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="First 7 Days" 
              fill="#94A3B8" 
              radius={[4, 4, 0, 0]}
              animationDuration={1000}
            />
            <Bar 
              dataKey="Last 7 Days" 
              radius={[4, 4, 0, 0]}
              animationDuration={1000}
            >
              {data.map((entry, index) => {
                const isImproved = invertColors 
                  ? entry["Last 7 Days"] < entry["First 7 Days"]
                  : entry["Last 7 Days"] > entry["First 7 Days"];
                return (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={isImproved ? '#059669' : '#DC2626'}
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Cost reduction summary */}
      <div className="mt-4 grid grid-cols-2 gap-4">
        {data.map((item, index) => {
          const change = item["First 7 Days"] > 0 
            ? ((item["Last 7 Days"] - item["First 7 Days"]) / item["First 7 Days"] * 100).toFixed(0)
            : 0;
          const isImproved = invertColors 
            ? item["Last 7 Days"] < item["First 7 Days"]
            : item["Last 7 Days"] > item["First 7 Days"];
          
          return (
            <div key={index} className={`p-3 rounded-lg ${
              isImproved ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'
            }`}>
              <p className="text-sm text-slate-600 mb-1">{item.metric}</p>
              <div className="flex items-center gap-2">
                {isImproved ? (
                  <TrendingDown className="w-4 h-4 text-emerald-600" />
                ) : (
                  <TrendingUp className="w-4 h-4 text-red-600" />
                )}
                <span className={`font-heading font-bold ${
                  isImproved ? 'text-emerald-700' : 'text-red-700'
                }`}>
                  {change}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default CostEfficiencyChart;
